// ===========================================
// The breadcrumb ROOT follows the view (roles slice 2, #434)
// ===========================================
// A page names its first crumb by a bare view root (`/admin`, `/teacher`, `/parent`,
// `/student`). That crumb is the view the user is IN, read from the URL, never the page's guess
// or the login role: 31 admin pages hard-code `{ label: 'Admin', path: '/admin' }` and also render
// in other views, and Account Settings labelled its crumb by `user.role` (a staff login on
// /admin/account saw "Teacher"). Every crumb under a view root carries the school slug: without
// it the link renders NotFound (#434). Pure, so it is pinned without rendering.

export const VIEW_ROOTS = ['admin', 'teacher', 'parent', 'student'] as const;
export type ViewRoot = (typeof VIEW_ROOTS)[number];

export interface Crumb {
  label: string;
  path?: string;
}

const isViewRoot = (s: string | undefined): s is ViewRoot => !!s && (VIEW_ROOTS as readonly string[]).includes(s);

/** The view the URL is in (`/:slug/<view>/…`), or null outside one. */
export function viewFromPath(pathname: string): ViewRoot | null {
  const section = pathname.split('/')[2];
  return isViewRoot(section) ? section : null;
}

/** The root crumb's label: the view's name as the view switcher says it. */
export function viewRootLabel(view: ViewRoot, user: { role?: string; isStaff?: boolean } | null | undefined): string {
  switch (view) {
    case 'admin':
      // /admin hosts the owner's full view and the staff view (`resolveEffectiveView`).
      return user?.isStaff && user.role !== 'ADMIN' ? 'Staff' : 'Admin';
    case 'teacher':
      return 'Teacher';
    case 'parent':
      return 'Parent';
    case 'student':
      return 'Student';
  }
}

/**
 * Rewrite a page's crumbs for where the user is: a bare view-root crumb becomes the CURRENT view's
 * root, and every path under a view root gains the school slug.
 */
export function resolveCrumbs(
  crumbs: readonly Crumb[],
  pathname: string,
  schoolSlug: string | null | undefined,
  user: { role?: string; isStaff?: boolean } | null | undefined
): Crumb[] {
  const current = viewFromPath(pathname);
  return crumbs.map((crumb) => {
    if (!crumb.path) return crumb;
    let { label, path } = crumb;
    // A page that already built the slug in (`/${slug}/admin`) is a view root too (step 9 /qa L4).
    if (schoolSlug && (path === `/${schoolSlug}` || path.startsWith(`/${schoolSlug}/`))) {
      path = path.slice(schoolSlug.length + 1) || '/';
    }
    const bare = path.replace(/^\//, '');
    if (current && isViewRoot(bare)) {
      label = viewRootLabel(current, user);
      path = `/${current}`;
    }
    const first = path.split('/')[1];
    if (schoolSlug && isViewRoot(first)) {
      path = `/${schoolSlug}${path}`;
    }
    return { label, path };
  });
}
